import React from "react";
import { useState, useEffect, useRef } from 'react';
import "./classes/SidebarQuickForm.css";
import { Helmet } from "react-helmet";
import { configData } from "../config";
import { getGlobalValue, subscribeGlobalContent } from './PageEditor/globalContentStore';
import PhoneInput, {
    formatPhoneNumber,
    formatPhoneNumberIntl,
    isPossiblePhoneNumber,
    isValidPhoneNumber,getCountryCallingCode
} from 'react-phone-number-input';
import Select from 'react-dropdown-select';
import ReCAPTCHA from "react-google-recaptcha";
import TextField from "./atomic/molecules/TextField";
import SelectField from "./atomic/molecules/SelectField";
import PhoneField from "./atomic/molecules/PhoneField";
import ConsentCheckbox from "./atomic/molecules/ConsentCheckbox";
import { useQuickApp, submitQuickAppStep1, isBlockedEmail, QUICK_APP_MESSAGES } from "./services/QuickAppService";
import { loadCountryStateLists, CANADIAN_PROVINCES } from "./services/CountryStateLists";
import { openQuickAppPopup } from "./QuickAppPopup";
import smwwLogo from "../images/quick_form/new_smww_logo.webp";

// The live forms' phone field (intl-tel-input with a separate dial code, its flag hidden by the
// theme): a grey box holding the country's calling code ("+1") and a small arrow, the number typed
// beside it. So the country box shows the calling code where the library draws a flag (and, like
// live's picker, offers no "International" choice). The course body's Apply Now form uses it too
// (AdvancedCertificateLeadForm.js).
export const DialCode = ({ country }) => (
    country ? <span className="PhoneInputDialCode">+{getCountryCallingCode(country)}</span> : null
);

// Degree pages with the general quick form in their right column on the live site. Their leads
// are filed as "degree" leads under the page's node id, as on the Drupal form.
const DEGREE_PAGES = [
    274987, // /masters-professional-studies-sports-management-degree (Fort Hays State)
    80442, // /sports-management-mba-degree (Concordia University Chicago)
    779, // /sports-management-masters-degree (Concordia University Chicago)
    780, // /sports-management-doctorate-degree (Concordia University Chicago)
    555137, // /content/bachelor-arts-sports-management (Concordia University Chicago)
];
export const isDegreePage = (page) => DEGREE_PAGES.includes(Number(page?.nid));

// FAQ pages: basic pages at /faq or at an address ending in /faq
export const isFaqPage = (page) => page?.type === "page" && /(^|\/)faq$/.test(page?.url_slug || "");

// The FAQ pages the live site shows its general quick form beside (block 144): all but the FAQs it
// made as their own content type in October 2026, which have no sidebars. Their leads are FAQ leads
// under the course the page answers for (the API's faq_course), as on the Drupal form.
const NO_FAQ_QUICK_FORM_PAGES = [
    555146, // /courses/flag-football-leadership/faq
    555147, // /courses/soccer-agent-course/faq
    555148, // /courses/soccer-player-development/faq
    555150, // /courses/sports-career-strategies/faq
];
export const hasFaqQuickForm = (page) => isFaqPage(page) && !NO_FAQ_QUICK_FORM_PAGES.includes(Number(page?.nid));

// Basic pages with the live site's general form beside them too: Your Path to Success as a
// Football Coach (live's course_page node: block 139 in the left column under the mentors, the
// course pages' form for a course without a product, so the general wording with the Text-me box,
// filed as a course lead with no sku) and the course testimonials pages (block 145 at the top of
// the right column, no Text-me box, filed as a "testimonial" lead with sku "testimonial", as
// live's hidden fields have it)
export const COURSE_PAGE_FORM_PAGES = [
    222384, // /content/your-path-success-football-coach
];
export const TESTIMONIALS_FORM_PAGES = [
    2062, // /courses/athlete-management-sports-agent/testimonials
    2063, // /courses/baseball-gm-scouting/testimonials
    2067, // /courses/football-gm-scouting/testimonials
];

// Course pages outside /courses (the apprenticeships), the degree pages, the FAQ pages and the
// basic pages above: the live site shows its general quick form there, in the right column (in
// place of a course's own form in the left one)
export const hasGeneralQuickForm = (page) =>
    (page?.type === "course" && !(page?.url_slug || "").startsWith("courses/")) || isDegreePage(page) || hasFaqQuickForm(page)
    || COURSE_PAGE_FORM_PAGES.includes(Number(page?.nid)) || TESTIMONIALS_FORM_PAGES.includes(Number(page?.nid));
// The general form has no "Text me with news and offers" box on the live site, except the
// football coach page's, which is the course pages' form
const hasTextMeBox = (page) => !hasGeneralQuickForm(page) || COURSE_PAGE_FORM_PAGES.includes(Number(page?.nid));

// Course pages outside /courses that have no quick form at all on the live site (they keep the
// apprenticeships' layout)
const NO_QUICK_FORM_PAGES = [
    131331, // /virtual-internship-program
    131332, // /sports-internship
    135668, // /internships-with-sports-management-worldwide
    3484, // /sports-analytics
];
export const hasNoQuickForm = (page) => NO_QUICK_FORM_PAGES.includes(Number(page?.nid));

// The live site's phone form breaks two lines of its copy (its template's <br />s): "Learn More
// About Attending / This Conference" and "The SMWW Team will contact you / within ...". The
// break shows only in the phone skin (.smww-mobile-break in SidebarQuickForm.css).
const withMobileBreak = (text, before) => {
    const at = typeof text === "string" ? text.indexOf(before) : -1;
    if (at <= 0) return text;
    return <>{text.slice(0, at)}<br className="smww-mobile-break" />{text.slice(at)}</>;
};

// phoneForm: the form a page body's [mobile_quick_form] shows on phones (Body.js), live's own
// phone form (its mob_ fields), whose state list starts differently from the other forms'
const SidebarQuickForm = ({ className = "",page, phoneForm = false  }) => {

    const [isOpen, setIsOpen] = useState(false);
    const recaptchaRef = useRef(null);
    const sitekey = configData.GOOGLE_RECAPTCHA_SITE_KEY;

    const initialForm = {
        fname:'',
        lname: '',
        email: '',
        // Empty: the calling code is shown in the country box, not in the number
        phone: '',
        fteam:'',
        fcity:'',
        fcountry:'United States',
        fstate:'',
        fprovince:'',
        // "Text me with news and offers" starts unticked, as on the live site's forms
        field_text_me_with_news_offers: 0,
        sku:'',
        page_type:'',
        showStateField: true,
        showProvinceField: false,
        pageURL: window.location.href,
    };

    const [formData, setFormData] = useState(initialForm);
    const [countriesList, setCountriesList] = useState([]);
    const [statelist, setStateList] = useState([]);
    const [errors, setErrors] = useState({});
    const [content, setContent] = useState({});
    const [saving, setSaving] = useState(false);
    // The form is step 1 of the application: once it is saved, steps 2 and 3 follow in a popup
    // and every quick form on the page shows where the application stands instead of its fields
    const application = useQuickApp();
    // Bumped when Global Course Content is hydrated or an admin saves, so the
    // shared marketing copy below rebuilds with the live values.
    const [gccVersion, setGccVersion] = useState(0);
    useEffect(() => subscribeGlobalContent(() => setGccVersion((v) => v + 1)), []);

    const displayMode = content?.display || "sidebar";
    useEffect(() => {
    if (page?.field_application_key_name) {
        setFormData(prev => ({
            ...prev,
            sku: page.field_application_key_name,
            page_type:page.type
        }));
    }
     if (page?.course_name) {
     // The course's short name, as the live site's forms show it (its store product's name,
     // "Football GM and Scouting"); the course list's name when the course has no product
     const courseName = page?.quick_form_course_name || page?.course_name;
     setContent({
        // Marketing microcopy is shared across every course page — resolved
        // from Global Course Content (with the same defaults as fallback). Only
        // the heading stays course-specific: it interpolates the course name.
        eyebrow: getGlobalValue('quick_form_eyebrow'),
        heading: "Learn More About Becoming "+ (/^[aeiou]/i.test(courseName) ? "an " : "a ") + courseName +" Student",
        description: getGlobalValue('quick_form_description'),
        ctaText: getGlobalValue('quick_form_cta_text'),
        footer: getGlobalValue('quick_form_footer'),
        display: "sidebar",
    });
}
    // The general form: the lead is still filed under the course, only the wording differs.
    // It has no "Text me with news and offers" box on the live site, so nobody is signed up
    // for texts from it.
    if (hasGeneralQuickForm(page)) {
        setFormData(prev => ({
            ...prev,
            field_text_me_with_news_offers: 0,
            ...(isDegreePage(page) ? { sku: String(page.nid), page_type: "degree" }
                : TESTIMONIALS_FORM_PAGES.includes(Number(page?.nid)) ? { sku: "testimonial", page_type: "testimonial" }
                : COURSE_PAGE_FORM_PAGES.includes(Number(page?.nid)) ? { sku: "", page_type: "course" }
                : {}),
        }));
        setContent({
            eyebrow: getGlobalValue('quick_form_eyebrow'),
            heading: "Learn More About Becoming a SMWW Student",
            description: "We want to help you find the right course for your career. Fill out the form below and an SMWW Career Counselor will follow up directly with you.",
            ctaText: getGlobalValue('quick_form_cta_text'),
            footer: "The SMWW Team will contact you within one business day.",
            display: "sidebar",
        });
    }
    // FAQ pages: the course the page answers for is named in the heading, and the lead is an FAQ
    // lead under its code, as on the Drupal form. /faq and the NHL Draft conference FAQ answer for
    // no course: the general heading, and the lead goes under no course, as there.
    if (hasFaqQuickForm(page)) {
        const course = page.faq_course;
        setFormData(prev => ({
            ...prev,
            sku: course?.sku || "",
            page_type: "course_faq",
        }));
        if (course?.name) {
            setContent(prev => ({
                ...prev,
                heading: "Learn More About Becoming " + (/^[aeiou]/i.test(course.name) ? "an " : "a ") + course.name + " Student",
            }));
        }
    }
    // Conference pages: the lead is filed under the conference's title, as on the Drupal form
    if (page?.type === "conferences") {
        setFormData(prev => ({
            ...prev,
            sku: page.title,
            page_type: "conference"
        }));
        setContent({
            eyebrow: getGlobalValue('quick_form_eyebrow'),
            heading: "Learn More About Attending This Conference",
            description: "Fill out the form below and an SMWW Career Counselor will follow up directly with you.",
            ctaText: getGlobalValue('quick_form_cta_text'),
            footer: "The SMWW Team will contact you within one business day.",
            display: "sidebar",
        });
    }
    }, [page, gccVersion]);
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'fcountry') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                showProvinceField: !(value === 'United States' || value === 'Canada'),
                showStateField: (value === 'United States' || value === 'Canada'),
                fprovince: (value === 'United States' || value === 'Canada') ? '' : prev.fprovince,
                // Back to the list's first entry, as live's changeCountry sets it (the list is
                // another country's now)
                fstate: '',
            }));
        } else if (type === 'checkbox') {
            if (name === "field_text_me_with_news_offers") {
                setFormData(prev => ({ ...prev, [name]: checked ? 1 : 0 }));
            } else {
                setFormData(prev => ({ ...prev, [name]: checked }));
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    useEffect(() => {
   
    }, []);

    // The states come listed by name, as the live forms list them
    useEffect(() => {
        loadCountryStateLists().then(({ countries, states }) => {
            setCountriesList(countries);
            setStateList(states);
        });
    }, []);

    const validate = (data) => {
        const newErrors = {};
        // const recaptchaValue = inputRefs.current['recaptcha'].getValue();
        const recaptchaValue = recaptchaRef.current.getValue();
        if (!data.fname.trim()) {
            newErrors.fname = 'First name is required.';
        }
        if (!data.lname.trim())  { 
            newErrors.lname = 'Last name is required.';
        }

        if (!data.email.trim()) {
            newErrors.email = 'Email is required.';
        } else if (!/\S+@\S+\.\S+/.test(data.email)) {
            newErrors.email = 'Please enter a valid email address';
        } else if (isBlockedEmail(data.email)) {
            newErrors.email = QUICK_APP_MESSAGES.email_blocked;
        }
        if (!data.phone || data.phone.trim() === '') {
            // The message the field gave when left with only its "+1" (it could not be emptied before)
            newErrors.phone = 'Please enter your phone number';
        } else {
            const digitsOnly = data.phone.replace(/\D/g, '');

            try {
                let codeLength = 0;

                if (formData.PrimaryPhoneCountry) {
                    codeLength = getCountryCallingCode(formData.PrimaryPhoneCountry).length;
                } else {
                    const match = digitsOnly.match(/^\d{1,3}/);
                    if (match) codeLength = match[0].length;
                }

                const numberWithoutCode = digitsOnly.slice(codeLength);

                if (!numberWithoutCode || numberWithoutCode.length === 0) {
                    console.log('Only country code entered');
                    newErrors.phone = 'Please enter your phone number';
                    data.phone = '';
                } else if (!isValidPhoneNumber(data.phone)) {
                    newErrors.phone = 'Invalid Phone Number';
                }

            } catch {
                newErrors.phone = 'Invalid Phone Number';
            }
        }

        if (!data.fteam.trim())  { 
            newErrors.fteam = 'Please tell us your favorite team.';
        }

        if (!data.fcity.trim())  { 
            newErrors.fcity = 'City is required.';
        }

        if (!data.fcountry) {
            newErrors.fcountry = 'Please select a country.';
        } else if (data.fcountry === 'United States' || data.fcountry === 'Canada') {
            // "-" is "Outside the US or Canada", which is not a state of the country chosen
            if (!data.fstate || data.fstate === '-') {
                newErrors.fstate = 'Please select a state.';
            }
        } else {
           /* if (!data.fprovince.trim()) {
                newErrors.fprovince = 'Please enter your province.';
            } */
        }
         if (!recaptchaValue) {
            newErrors.recaptcha = 'Please verify the reCAPTCHA.';
        } 
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;

        const formErrors = validate(formData);

        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
        } else {
            setErrors({});
            setSaving(true);
            // Step 1 of the application (application/step1), as the Drupal quick form posts it
            const saved = await submitQuickAppStep1(formData, countriesList);
            setSaving(false);
            if (saved) {
                // Optional: reset form
                setFormData(initialForm);
                openQuickAppPopup();
            } else {
                setErrors({ save: QUICK_APP_MESSAGES.save });
            }
        }
    };


const formContent = (
    <>
        {application ? (
            application.stage === 4 ? (
            <div className="ctct-form-success">
                <h2 className="smww-heading">{getGlobalValue('quick_form_success_heading')}</h2>
                <p className="ctct-form-text">{getGlobalValue('quick_form_success_message')}</p>
            </div>
            ) : (
            <div className="sqa-resume">
                <p>Thank you, {application.state.first_name}! You’re one step in.</p>
                <button type="button" className="sqa-btn" onClick={openQuickAppPopup}>Finish your application</button>
            </div>
            )
        ) : (<>
            <header className="smww-form-header" data-atomic="molecule" data-atomic-name="form-header">
                <span className="smww-eyebrow" data-atomic="atom" data-atomic-name="eyebrow">
                    {content.eyebrow}
                </span>
                <h2 className="smww-heading" data-atomic="atom" data-atomic-name="heading">
                    {withMobileBreak(content.heading, "This Conference")}
                </h2>
                <p className="smww-text smww-text--body" data-atomic="atom" data-atomic-name="body-text">
                    {content.description}
                </p>
            </header>
            <form className="smww-form" data-atomic="organism" data-atomic-name="quick-form">
                <div className={displayMode === "sidebar" ? "smww-grid smww-grid-side" : "smww-grid-modal"}>
                    {/* 1. First Name */}
                    <TextField
                        label="First Name"
                        id="dfname"
                        name="fname"
                        placeholder=" First Name"
                        value={formData.fname}
                        onChange={handleChange}
                        required
                        error={errors.fname}
                    />

                    {/* 2. Last Name */}
                    <TextField
                        label="Last Name"
                        id="dlname"
                        name="lname"
                        placeholder=" Last Name"
                        value={formData.lname}
                        onChange={handleChange}
                        required
                        error={errors.lname}
                    />

                    {/* 3. Email */}
                    <TextField
                        label="Email"
                        id="demail"
                        name="email"
                        type="email"
                        placeholder=" Email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        error={errors.email}
                    />

                    {/* 4. Phone */}
                    <PhoneField label="Phone" required error={errors.phone}>
                        {/* The calling code sits in the country box (DialCode), so the number field
                            starts empty with its placeholder, as live's; the value is still the full
                            international number ("+15035551234"), empty until digits are typed */}
                        <PhoneInput
                            placeholder=" Phone"
                            className="smww-input smww-phone-dial"
                            flagComponent={DialCode}
                            addInternationalOption={false}
                            value={formData.phone}
                            defaultCountry={formData.PrimaryPhoneCountry || 'US'}
                            onCountryChange={(country) => {
                                setFormData((prev) => ({ ...prev, PrimaryPhoneCountry: country }));
                            }}
                            onChange={(phone) => {
                                setFormData((prev) => ({ ...prev, phone: phone || '' }));
                            }}
                        />
                    </PhoneField>

                    {/* 5. Favorite Team or Club */}
                    <TextField
                        label="Favorite Team or Club"
                        id="fteam"
                        name="fteam"
                        placeholder=" Your Favorite Team"
                        value={formData.fteam}
                        onChange={handleChange}
                        required
                        error={errors.fteam}
                    />

                    {/* 6. City */}
                    <TextField
                        label="City"
                        id="fcity"
                        name="fcity"
                        placeholder=" City"
                        value={formData.fcity}
                        onChange={handleChange}
                        required
                        error={errors.fcity}
                    />

                    {/* 7. Country */}
                    <SelectField
                        label="Country"
                        id="fcountry"
                        name="fcountry"
                        value={formData.fcountry}
                        onChange={handleChange}
                        required
                        error={errors.fcountry}
                    >
                        <option value="">Select Country</option>
                        {Object.keys(countriesList).map(item => {
                            return <option key={countriesList[item].label} value={countriesList[item].label}>{countriesList[item].label}</option>
                        })}
                    </SelectField>

                    {/* 8. State */}
                    {formData.showStateField && (
                        <SelectField
                            label="State"
                            id="fstate"
                            name="fstate"
                            value={formData.fstate}
                            onChange={handleChange}
                            required
                            error={errors.fstate}
                        >
                            {/* Live's smww-quickapp.js narrows every quick form's list to the chosen
                                country's states (its own lists, with Wyoming in the US), under the
                                page's first entries: "-Select State/Province-" on the phone form (its
                                changeCountrymob loads Drupal's getstateList), "Select a State or
                                Province" and "Outside the US or Canada" on the others. The value is the
                                state's name, as the API takes it. */}
                            {phoneForm ? (
                                <option value="">-Select State/Province-</option>
                            ) : (
                                <>
                                    <option value="">Select a State or Province</option>
                                    <option value="-">Outside the US or Canada</option>
                                </>
                            )}
                            {statelist.filter(state => CANADIAN_PROVINCES.includes(state.abbreviation) === (formData.fcountry === 'Canada')).map(state => (
                                <option key={state.name} value={state.name}>{state.name}</option>
                            ))}
                        </SelectField>
                    )}

                    {/* 9. Province */}
                    {formData.showProvinceField && (
                        <TextField
                            label="Province"
                            name="fprovince"
                            placeholder=" Province"
                            value={formData.fprovince}
                            onChange={handleChange}
                            required
                            error={errors.fprovince}
                            inputClassName="smww-select"
                        />
                    )}
                </div>

                {/* SMS Sign-up Consent */}
                {hasTextMeBox(page) && (
                <ConsentCheckbox
                    label="Text me with news and offers"
                    id="text_with_me_news_offers"
                    name="field_text_me_with_news_offers"
                    defaultChecked={Number(formData.field_text_me_with_news_offers) === 1}
                    onChange={handleChange}
                />
                )}

                {/* 10. CAPTCHA Wrapper */}
                <div className="smww-recaptcha-container">
                    {/* <div className="recaptcha" style={{transform:"scale(0.65, 0.85)", transformOrigin:"0 0", marginBottom:"0"}}> */}
                    <div 
                        style={displayMode === "sidebar" ? {transform: "scale(0.57)", transformOrigin: "0 0", marginBottom: "0", } : {} }
                    >
                        <ReCAPTCHA
                            // ref={(element) => {inputRefs.current['recaptchaRef'] = element;}}
                            ref={recaptchaRef}
                            sitekey={sitekey}
                            size="visible"
                            onChange={handleChange}
                            // required
                        />
                    </div>
                    {errors.recaptcha &&
                        <span className="sidebar-quickform-error sidebar-quickform-error-phone">
                        {errors.recaptcha}
                        </span>
                    }
                </div>

                {/* Form Actions (Button cta + placeholder loading state) */}
                {/* Dimmed while saving, label unchanged, as on the Drupal form (its Google Tag Manager trigger matches the button by its text) */}
                <button className="smww-btn smww-btn--cta" type="submit" onClick={handleSubmit} aria-disabled={saving ? "true" : undefined} style={saving ? { opacity: 0.7 } : undefined} data-atomic="atom" data-atomic-name="button">
                    {content.ctaText}
                </button>
                {errors.save &&
                    <span className="sidebar-quickform-error" role="alert">
                    {errors.save}
                    </span>
                }

                {/* Disclaimer Text */}
                <p className="smww-text smww-text--muted smww-form-footer">
                    {withMobileBreak(content.footer, "within ")}
                </p>
            </form>
        </>)}
    </>
);

const handleClose = () => {
    setIsOpen(false);
    setFormData(initialForm);
    setErrors({});
};

    // return (
    //     <section className="smww-section course-sidebarform">
    //         <div className="smww-container">
    //             <div className="smww-surface">


    //             </div>
    //         </div>
    //     </section>
    // );
    if (displayMode === "sidebar") {
        return (
        <section className="smww-section course-sidebarform">
            <div className="smww-container">
                <div className="smww-surface">
                    {/* The SMWW globe above the form, an image with the live form's alt text (shown by the sidebar and phone skins) */}
                    <img className="smww-logo" src={smwwLogo} alt="SMWW_Logo" />
                    {formContent}
                </div>
            </div>
        </section>
        );
    }

    return (
        <div className="course-sidebarform">
        <button onClick={() => setIsOpen(true)} className={className}>
            {content.ctaText}
        </button>

        {isOpen && (
            <div className="modal course-sideform-modal" onClick={handleClose}>
                <div className="modal-content course-sidebarform" onClick={(e) => e.stopPropagation()}>
                    <button onClick={handleClose} className="course-sidebarform-close">×</button>
                    <div className="smww-surface smww-modal-surface">
                        {formContent}
                    </div>
                </div>
            </div>
        )}
        </div>
    );

};
export default SidebarQuickForm;